import React, { useEffect, useMemo, useState } from 'react';
import {
  createAdminUser,
  deleteAdminUser,
  listAdminUsers,
  updateAdminUserRole,
} from '../api/adminUsers';
import { ADMIN_ROLE_GROUPS, useAuth } from '../auth/AuthContext';

const FALLBACK_ROLES = ADMIN_ROLE_GROUPS;

function formatDate(value) {
  if (!value) return 'Not available';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function UsersPage() {
  const { tokens, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [form, setForm] = useState({ email: '', role: 'SUPPORT' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const canManageUsers = user?.activeRole === 'OWNER' || user?.activeRole === 'SUPPORT_ADMIN';
  const visibleRoles = useMemo(() => {
    if (user?.activeRole === 'OWNER') return roles;
    return roles.filter((role) => role !== 'OWNER');
  }, [roles, user?.activeRole]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listAdminUsers(tokens?.idToken);
      setUsers(data.users || []);
      setRoles(data.roles?.length ? data.roles : FALLBACK_ROLES);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.idToken]);

  const updateForm = (field) => (event) => {
    setError('');
    setNotice('');
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    const email = form.email.trim();
    if (!email) {
      setError('Email is required.');
      return;
    }

    try {
      setSaving('create');
      setError('');
      setNotice('');
      await createAdminUser(tokens?.idToken, { email, role: form.role });
      setForm({ email: '', role: visibleRoles.includes('SUPPORT') ? 'SUPPORT' : visibleRoles[0] });
      await loadUsers();
      setNotice(`Created admin user ${email}. Cognito will send the temporary password email.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  const changeRole = async (targetUser, role) => {
    try {
      setSaving(`role:${targetUser.username}`);
      setError('');
      setNotice('');
      await updateAdminUserRole(tokens?.idToken, targetUser.username, role);
      await loadUsers();
      setNotice(`Updated ${targetUser.email} to ${role}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  const removeUser = async (targetUser) => {
    const confirmed = window.confirm(`Delete admin user ${targetUser.email}?`);
    if (!confirmed) return;

    try {
      setSaving(`delete:${targetUser.username}`);
      setError('');
      setNotice('');
      await deleteAdminUser(tokens?.idToken, targetUser.username);
      setUsers((current) => current.filter((item) => item.username !== targetUser.username));
      setNotice(`Deleted admin user ${targetUser.email}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="page users-page">
      <div className="users-header">
        <div>
          <h2>Admin Users</h2>
          <p className="muted">Create internal Nice admin users and assign one active role.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={loadUsers} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {!canManageUsers ? (
        <div className="alert alert-warning">Only OWNER and SUPPORT_ADMIN roles can manage admin users.</div>
      ) : null}

      <form className="panel users-create-form" onSubmit={submitCreate}>
        <div>
          <div className="profile-label">Create New User</div>
          <div className="users-create-grid">
            <label>
              <span>Email</span>
              <input
                value={form.email}
                onChange={updateForm('email')}
                type="email"
                placeholder="name@niceforyou.com"
                disabled={!canManageUsers}
              />
            </label>
            <label>
              <span>Role</span>
              <select value={form.role} onChange={updateForm('role')} disabled={!canManageUsers}>
                {visibleRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn btn-primary" type="submit" disabled={!canManageUsers || saving === 'create'}>
              {saving === 'create' ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </form>

      <section className="panel users-table-panel">
        {loading ? (
          <div className="muted">Loading admin users...</div>
        ) : (
          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((adminUser) => {
                  const roleSaving = saving === `role:${adminUser.username}`;
                  const deleteSaving = saving === `delete:${adminUser.username}`;
                  const disableOwnerRole = user?.activeRole !== 'OWNER' && adminUser.role === 'OWNER';

                  return (
                    <tr key={adminUser.username}>
                      <td className="users-username">{adminUser.username}</td>
                      <td>{adminUser.email}</td>
                      <td>
                        <select
                          className={adminUser.roleError ? 'users-role-select users-role-select-error' : 'users-role-select'}
                          value={adminUser.role || ''}
                          disabled={!canManageUsers || roleSaving || disableOwnerRole}
                          onChange={(event) => changeRole(adminUser, event.target.value)}
                        >
                          <option value="" disabled>
                            Select role
                          </option>
                          {adminUser.role && !visibleRoles.includes(adminUser.role) ? (
                            <option value={adminUser.role}>{adminUser.role}</option>
                          ) : null}
                          {visibleRoles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        {adminUser.roleError ? <div className="users-role-error">{adminUser.roleError}</div> : null}
                      </td>
                      <td>
                        <span className={adminUser.enabled ? 'status-pill status-active' : 'status-pill'}>
                          {adminUser.status}
                        </span>
                      </td>
                      <td>{formatDate(adminUser.createdAt)}</td>
                      <td className="users-actions">
                        <button
                          className="btn btn-danger"
                          type="button"
                          disabled={!canManageUsers || deleteSaving || disableOwnerRole}
                          onClick={() => removeUser(adminUser)}
                        >
                          {deleteSaving ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!users.length ? (
                  <tr>
                    <td colSpan="6" className="users-empty">
                      No admin users found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
